import {BehaviorSubject, combineLatest, Subject} from 'rxjs';
import {filter, map, skipUntil, switchMap} from 'rxjs/operators';
import {env} from '@/helper/env';
import {mc} from '@/core';
import {operationApi} from '@/api';
import {isLogin$} from '@/streams/auth';
import {browser$} from '@/streams/browser';

const StorageKey = 'dialog_count';
const dialogCache = getDialogStorage();
const dialog$ = new BehaviorSubject([]);
const dialogList$ = dialog$.pipe(
  filter((v) => v.length > 0),
  map(cleanTemplateOutDate),
  map(initTemplateCache)
);
const noticeFrequencyConfig$ = new BehaviorSubject({globalMaxPerDay: 0, globalIntervalMinutes: 0});

export const dialogPageMount$ = new BehaviorSubject(false);
export const dialogStatus$ = new BehaviorSubject(false);
export const query$ = new Subject();
export const dialogInfo$ = combineLatest([dialogPageMount$, dialogList$, dialogStatus$]).pipe(
  filter(() => {
    return isMatchFrequencyConfig();
  }),
  map(getTemplateInRange),
  map(getFatigueIntervalTemplate),
  map(getHighestPriority),
  filter(Boolean)
);

isLogin$.pipe(filter(Boolean)).subscribe(async () => {
  const {data} = await operationApi.getFrequencyConfig();
  resetDialogCountCache();
  noticeFrequencyConfig$.next(data);
});

combineLatest([query$.pipe(skipUntil(browser$)), isLogin$, noticeFrequencyConfig$])
  .pipe(
    filter(() => {
      return isMatchFrequencyConfig();
    }),
    switchMap(([query, isLogin]) => (!isLogin ? [] : Promise.all([getDialogInfo(query), getTestDialogInfo(query)])))
  )
  .subscribe(([publishData = {}, testData]) => {
    if (testData && testData.rows) {
      dialog$.next([...convertData(publishData, 0), ...convertData(testData, 1)]);
      return;
    }
    dialog$.next(convertData(publishData, 0));
  });

async function getDialogInfo(query) {
  const {data, error} = await operationApi.getDialogDataList(query);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
    return {};
  }
}

async function getTestDialogInfo(query) {
  const {data, error} = await operationApi.getTestDialogDataList(query);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
    return {};
  }
}

function convertData(data, testing) {
  const {rows = []} = data;
  return rows.map((o) => {
    const {plan, config} = o;
    const {fatigueRule, priority, ...rest} = config || {};
    return {
      status: plan.status,
      platformList: plan.platformList,
      createTime: rest.createTime,
      startTime: plan.startTime,
      endTime: plan.endTime,
      platformAllPage: plan.platformRoute.web === 'ALL',
      platformWebRoutes: (plan.platformRoute.web && plan.platformRoute.web.split(',')) || [],
      fatigueType: fatigueRule.type === 'ONCE' ? 1 : 2,
      fatigueCount: fatigueRule.limitTimes || 1,
      fatigueIntervalSeconds: (fatigueRule.minIntervalMinutes || 1) * 60000,
      currentCount: 0,
      limitDays: (fatigueRule.limitDays || 1),
      priority: priority === 'LOW' ? -1 : priority === 'MID' ? 0 : 1,
      templateType: rest.templateType === 'DIALOG' ? 'image' : 'text',
      templateKey: `${!testing ? 't' : 'tt'}${rest.groupId}`,
      webPic: rest.webPic,
      appPic: rest.appPic,
      btn1: rest.btn1,
      btn2: rest.btn2,
      title: rest.title,
      content: rest.content,
      testing: !!testing,
    };
  });
}

function initTemplateCache(dataList) {
  if (dataList && dataList.length > 0) {
    dataList.reduce((p, o) => {
      if (!p.hasOwnProperty(o.templateKey)) {
        p[o.templateKey] = {count: 0, time: 0, totalCount: 0};
      }
      return p;
    }, dialogCache);
    return dataList;
  }
  return [];
}

function cleanTemplateOutDate(dataList) {
  const storageData = getDialogStorage();
  if (!dataList || dataList.length === 0) {
    return dataList;
  }
  const dataMap = dataList.reduce((p, o) => {
    p[o.templateKey] = true;
    return p;
  }, {});
  const cache = Object.keys(storageData).reduce((p, k) => {
    if (dataMap[k]) {
      p[k] = storageData[k];
    }
    return p;
  }, {});
  updateAllStorage(cache);
  return dataList;
}

function getFatigueIntervalTemplate([dataList, dialogStatus, milliseconds]) {
  const currentMilliseconds = Date.now();
  resetDialogCountCache();
  const filterDataList = dataList.filter((item) => {
    const dialogCacheTemplate = dialogCache[item.templateKey] || {count: 0, time: 0, totalCount: 0};
    if (dialogCacheTemplate.totalCount === 0) return true;

    // 1. 用户limitDays内最多展示y次
    // 间隔超过limitDays, resetDialogCache(item.templateKey)
    const {limitDays = 1} = item;
    if (currentMilliseconds - dialogCacheTemplate.time > limitDays * 24 * 60 * 60 * 1000) {
      resetDialogCache(item.templateKey);
      return true;
    }

    // 2. 针对设备，只展示一次
    if (item.fatigueType === 1) {
      return dialogCacheTemplate.count < 1;
    }

    // 3. limitDays内，判断疲劳时间和limitDays内展示次数
    const interval = currentMilliseconds - dialogCacheTemplate.time - item.fatigueIntervalSeconds;
    return interval >= 0 && dialogCacheTemplate.totalCount < item.fatigueCount;
  });

  return [filterDataList, dialogStatus, milliseconds];
}

function getTemplateInRange([, filterList, dialogStatus]) {
  const currentMilliseconds = new Date().valueOf();
  const filterDataList = filterList.filter((item) => {
    if (item.testing) {
      return true;
    }
    return item.startTime <= currentMilliseconds && item.endTime >= currentMilliseconds;
  });

  return [filterDataList, dialogStatus, currentMilliseconds];
}

/**
 * count 写入localstorage
 * */
function getHighestPriority([filterList, dialogStatus, currentMilliseconds]) {
  if (filterList && filterList.length > 0 && !dialogStatus) {
    return [filterList, validatePath, updateStorageFun(currentMilliseconds)];
  }
  return [];
}

function validatePath(filterList, currentPath) {
  const availableContents = filterList.filter((content) => content.platformAllPage || isRootPage(currentPath, content.platformWebRoutes));
  if (availableContents.length <= 0) {
    return null;
  }
  const sortDataList = sortByPriority(availableContents);
  return sortDataList[0];
}

function isRootPage(path, webRoutes = []) {
  return (
    webRoutes.filter((webRoute) => {
      if (webRoute === '/') {
        return path === webRoute;
      }
      return path.startsWith(webRoute);
    }).length > 0
  );
}

function updateStorageFun(currentMilliseconds) {
  return function (val) {
    updateDialogCache(val.templateKey, currentMilliseconds);
    updateDialogStorage(val.templateKey, currentMilliseconds);
  };
}

function sortByPriority(filterList) {
  return filterList.sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority > b.priority ? -1 : 0;
    }
    return a.createTime > b.createTime ? -1 : 0;
  });
}

// 取localstorage中dialog_count
function getDialogStorage() {
  if (env.isServer) {
    return {};
  }
  const dialogCountString = localStorage.getItem(StorageKey) || '';
  return dialogCountString.split(',').reduce((p, d) => {
    const [key, value] = d.split(':');
    if (key) {
      const [count, time, totalCount] = (value || '').split('_');
      p[key] = {count: Number(count || 0), time: Number(time || 0), totalCount: Number(totalCount || 0)};
    }
    return p;
  }, {});
}

function resetDialogCountCache() {
  const currentMilliseconds = Date.now();
  for (const templateKey in dialogCache) {
    if (currentMilliseconds - dialogCache[templateKey].time > 24 * 60 * 60 * 1000) {
      dialogCache[templateKey].count = 0;
    }
  }
  updateAllStorage(dialogCache);
}

function updateDialogCache(templateKey, currentMilliseconds) {
  if (templateKey in dialogCache) {
    const dialogCacheInfo = dialogCache[templateKey];
    dialogCache[templateKey].totalCount = dialogCacheInfo.totalCount + 1;
    // 一天之内count + 1
    if (currentMilliseconds - dialogCache[templateKey].time < 24 * 60 * 60 * 1000) {
      dialogCache[templateKey].count = dialogCacheInfo.count + 1;
    } else {
      // 一天之外count 重置为1
      dialogCache[templateKey].count = 1;
    }
    dialogCache[templateKey].time = currentMilliseconds;
  } else {
    dialogCache[templateKey] = {count: 1, time: currentMilliseconds, totalCount: 1};
  }
}

function resetDialogCache(templateKey) {
  if (templateKey in dialogCache) {
    delete dialogCache[templateKey];
    updateAllStorage(dialogCache);
  }
}

function updateDialogStorage(key, time) {
  const template = dialogCache[key];
  const dialogStorage = getDialogStorage();
  dialogStorage[key] = {count: template.count, time, totalCount: template.totalCount};
  updateAllStorage(dialogStorage);
}

function updateAllStorage(dialogStorage) {
  const dialogStr = Object.keys(dialogStorage)
    .map((key) => `${key}:${dialogStorage[key].count}_${dialogStorage[key].time}_${dialogStorage[key].totalCount}`)
    .join(',');
  localStorage.setItem(StorageKey, dialogStr);
}

function isMatchFrequencyConfig() {
  const {globalMaxPerDay, globalIntervalMinutes} = noticeFrequencyConfig$.getValue();
  let sumCount = 0;
  let lastDisplay = 0;
  for (const key in dialogCache) {
    const {count, time} = dialogCache[key];
    if (time - Date.now() < 24 * 60 * 60 * 1000) {
      sumCount += count;
      lastDisplay = Math.max(lastDisplay, time);
    }
  }
  return sumCount < globalMaxPerDay && Date.now() - lastDisplay > globalIntervalMinutes * 60 * 1000;
}
